import { useEffect, useState } from 'react';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
import { apiMutate } from '../../../lib/csrf-fetch';
import { lerErroApi } from '../../../lib/vendas/erro-api';
import { validarEncerramentoParcial } from './encerrar-op-parcial';

interface EncerrarOpParcialDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly opId: string;
  readonly produzido: number;
  readonly pendente: number;
  readonly onSucesso: () => void;
}

export function EncerrarOpParcialDialog({ aberto, onAbertoChange, opId, produzido, pendente, onSucesso }: EncerrarOpParcialDialogProps) {
  const [saldo, setSaldo] = useState(String(pendente));
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => { if (aberto) { setSaldo(String(pendente)); setJustificativa(''); setErro(null); } }, [aberto, pendente]);

  const confirmar = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const encerramento = validarEncerramentoParcial(saldo, justificativa, pendente);
    if (typeof encerramento === 'string') { setErro(encerramento); return; }
    setEnviando(true); setErro(null);
    try {
      const resposta = await apiMutate(`/api/v1/producao/ops/${encodeURIComponent(opId)}/encerrar-parcial`, {
        method: 'POST', body: encerramento,
      });
      if (!resposta.ok) {
        setErro((await lerErroApi(resposta, 'Não foi possível encerrar parcialmente a OP.')).mensagem);
        return;
      }
      onAbertoChange(false);
      onSucesso();
    } catch (causa) {
      setErro(causa instanceof Error && causa.message ? causa.message : 'Não foi possível encerrar parcialmente a OP.');
    } finally { setEnviando(false); }
  };

  return <FormDialog open={aberto} onOpenChange={(novo) => { if (!enviando) onAbertoChange(novo); }}>
    <FormDialogContent>
      <form onSubmit={(event) => void confirmar(event)} noValidate>
        <FormDialogHeader><FormDialogTitle>Encerrar OP parcialmente</FormDialogTitle></FormDialogHeader>
        <FormDialogBody>
          <p className="text-sm text-muted-foreground">Produzido: {produzido}. Pendente: {pendente}.</p>
          <label className="mt-3 block text-sm font-medium" htmlFor="encerrar-parcial-saldo">Saldo não produzido a devolver</label>
          <input id="encerrar-parcial-saldo" type="number" min="0.0001" max={pendente} step="any" value={saldo} disabled={enviando} onChange={(event) => setSaldo(event.target.value)} className="mt-1 w-full rounded border p-2" />
          <label className="mt-3 block text-sm font-medium" htmlFor="encerrar-parcial-justificativa">Justificativa</label>
          <textarea id="encerrar-parcial-justificativa" required minLength={5} value={justificativa} disabled={enviando} onChange={(event) => setJustificativa(event.target.value)} className="mt-1 min-h-24 w-full rounded border p-2" />
          {erro && <p role="alert" className="mt-2 text-sm text-destructive">{erro}</p>}
        </FormDialogBody>
        <FormDialogFooter><button type="button" disabled={enviando} onClick={() => onAbertoChange(false)}>Cancelar</button><button type="submit" disabled={enviando}>{enviando ? 'Encerrando…' : 'Encerrar parcialmente'}</button></FormDialogFooter>
      </form>
    </FormDialogContent>
  </FormDialog>;
}
